import React, { useState, useEffect, useContext } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Gavel, Clock, MapPin, AlertTriangle, ChevronRight, 
  Eye, FileText, Download, User, Calendar, Shield,
  CheckCircle, Info, TrendingUp, ChevronDown, ChevronUp, Users, Settings
} from 'lucide-react';

// Helper to build full image URL
const getImageUrl = (url, API) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  const baseUrl = API.replace('/api', '');
  return `${baseUrl}${url}`;
};

export default function LotPage() {
  const { vacationSlug, lotSlug } = useParams();
  const { API, user, BACKEND_URL } = useContext(AppContext);
  const [lot, setLot] = useState(null);
  const [vacation, setVacation] = useState(null);
  const [offres, setOffres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(null);
  const [inscription, setInscription] = useState(null);
  
  // Bid form
  const [bidAmount, setBidAmount] = useState('');
  const [maxAutoBid, setMaxAutoBid] = useState('');
  const [useAutoBid, setUseAutoBid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // UI state
  const [showAllSpecs, setShowAllSpecs] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Helper to get full URL for uploaded files
  const getFileUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    // URL is already in format /api/uploads/... - just prepend BACKEND_URL
    return `${BACKEND_URL}${url}`;
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchOffres, 5000); // Polling every 5 seconds
    return () => clearInterval(interval);
  }, [lotSlug]);

  useEffect(() => {
    if (!vacation) return;

    const updateCountdown = () => {
      if (vacation.statut !== 'vente_en_cours') {
        setCountdown(null);
        return;
      }

      const now = new Date();
      const targetDate = new Date(vacation.date_cloture_vente);
      const diff = targetDate - now;

      if (diff <= 0) {
        setCountdown({ expired: true });
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setCountdown({
        hours,
        minutes,
        seconds,
        isUrgent: diff < 120000
      });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [vacation]);

  const fetchData = async () => {
    try {
      const [lotRes, vacRes] = await Promise.all([
        axios.get(`${API}/encheres/lots/${lotSlug}`),
        axios.get(`${API}/encheres/vacations/${vacationSlug}`)
      ]);
      setLot(lotRes.data);
      setVacation(vacRes.data);
      
      await fetchOffres();
      
      // Check user inscription if logged in
      if (user) {
        await checkInscription(vacRes.data.id);
      }
    } catch (error) {
      console.error('Error fetching lot:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOffres = async () => {
    try {
      const response = await axios.get(`${API}/encheres/lots/${lotSlug}/offres`);
      setOffres(response.data || []);
    } catch (error) {
      console.error('Error fetching offres:', error);
    }
  };

  const checkInscription = async (vacationId) => {
    try {
      const response = await axios.get(`${API}/encheres/mes-inscriptions?acheteur_id=${user.id}`);
      const found = (response.data || []).find(i => i.vacation_id === vacationId);
      setInscription(found);
    } catch (error) {
      console.error('Error checking inscription:', error);
    }
  };

  const handleBid = async () => {
    if (!user) {
      toast.error('Vous devez être connecté pour enchérir');
      return;
    }

    if (!inscription || inscription.statut !== 'validee') {
      toast.error('Votre dossier doit être validé pour enchérir');
      return;
    }

    const amount = parseInt(bidAmount);
    const minBid = lot.meilleure_offre 
      ? lot.meilleure_offre + lot.pas_enchere 
      : lot.mise_a_prix;

    if (!amount || amount < minBid) {
      toast.error(`Votre offre doit être d'au moins ${formatPrice(minBid)} €`);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        lot_id: lot.id,
        montant: amount
      };
      
      if (useAutoBid && maxAutoBid) {
        payload.montant_max_auto = parseInt(maxAutoBid);
      }

      await axios.post(`${API}/encheres/offres?acheteur_id=${user.id}`, payload);
      toast.success('Votre offre a été enregistrée');
      setBidAmount('');
      await fetchOffres();
      await fetchData(); // Refresh lot data
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erreur lors du dépôt de l\'offre';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR').format(price);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#F0A22E]"></div>
      </div>
    );
  }

  if (!lot || !vacation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Gavel className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h1 className="text-2xl font-bold text-gray-700 mb-2">Lot non trouvé</h1>
          <Link to="/encheres">
            <Button>Retour aux enchères</Button>
          </Link>
        </div>
      </div>
    );
  }

  const minBid = lot.meilleure_offre 
    ? lot.meilleure_offre + lot.pas_enchere 
    : lot.mise_a_prix;

  const canBid = user && inscription?.statut === 'validee' && 
                 vacation.statut === 'vente_en_cours' &&
                 (lot.mode_visite !== 'obligatoire' || inscription?.visite_effectuee);

  // Schema.org Product (with structured fields)
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": lot.titre_fr,
    "description": lot.description_vendeur_fr?.slice(0, 500),
    "image": lot.photos?.filter(p => p.url).map(p => p.url.startsWith('http') ? p.url : `https://geots.consulting${p.url}`) || [],
    "brand": lot.marque || lot.caracteristiques?.find(c => c.label.toLowerCase().includes('marque'))?.valeur,
    "model": lot.modele,
    "productionDate": lot.annee_mise_service ? `${lot.annee_mise_service}` : undefined,
    "itemCondition": "https://schema.org/UsedCondition",
    "category": "Matériel géotechnique",
    "url": `https://geots.consulting/encheres/${vacationSlug}/${lotSlug}`,
    "offers": {
      "@type": "Offer",
      "priceCurrency": "EUR",
      "price": lot.mise_a_prix,
      "priceValidUntil": vacation.date_cloture_vente,
      "availability": lot.statut === 'attribue' 
        ? "https://schema.org/SoldOut" 
        : "https://schema.org/InStock",
      "itemCondition": "https://schema.org/UsedCondition",
      "seller": {
        "@type": "Organization",
        "name": "GEOTS Consulting"
      }
    }
  };

  // Add location if available
  if (lot.commune) {
    productSchema.offers.availableAtOrFrom = {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": lot.commune,
        "addressRegion": lot.departement,
        "addressCountry": "FR"
      }
    };
  }

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://geots.consulting/" },
      { "@type": "ListItem", "position": 2, "name": "Enchères", "item": "https://geots.consulting/encheres" },
      { "@type": "ListItem", "position": 3, "name": vacation.titre_fr, "item": `https://geots.consulting/encheres/${vacationSlug}` },
      { "@type": "ListItem", "position": 4, "name": lot.titre_fr, "item": `https://geots.consulting/encheres/${vacationSlug}/${lotSlug}` }
    ]
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{lot.seo_titre || `${lot.titre_fr} — mise à prix ${formatPrice(lot.mise_a_prix)} €`} | Enchères GEOTS</title>
        <meta 
          name="description" 
          content={lot.seo_description || `${lot.titre_fr}. Mise à prix ${formatPrice(lot.mise_a_prix)} €. ${lot.commune || ''} ${lot.departement ? `(${lot.departement})` : ''}`} 
        />
        <link rel="canonical" href={`https://geots.consulting/encheres/${vacationSlug}/${lotSlug}`} />
        
        {/* Open Graph */}
        <meta property="og:title" content={lot.seo_titre || lot.titre_fr} />
        <meta property="og:description" content={`Mise à prix ${formatPrice(lot.mise_a_prix)} €. ${lot.description_vendeur_fr?.slice(0, 150) || ''}`} />
        <meta property="og:type" content="product" />
        <meta property="og:url" content={`https://geots.consulting/encheres/${vacationSlug}/${lotSlug}`} />
        <meta property="og:site_name" content="GEOTS Consulting" />
        {lot.photos?.[0]?.url && <meta property="og:image" content={getFileUrl(lot.photos[0].url)} />}
        <meta property="product:price:amount" content={lot.mise_a_prix} />
        <meta property="product:currency" content="EUR" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={lot.seo_titre || lot.titre_fr} />
        <meta name="twitter:description" content={`Mise à prix ${formatPrice(lot.mise_a_prix)} €`} />
        {lot.photos?.[0]?.url && <meta name="twitter:image" content={getFileUrl(lot.photos[0].url)} />}
        
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Legal Banner - Always visible */}
      <div className="bg-[#0A2540] text-white">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-start gap-3 text-sm">
            <AlertTriangle className="w-5 h-5 text-[#F0A22E] flex-shrink-0 mt-0.5" />
            <p>
              <strong>Vente par courtage aux enchères — vente de gré à gré.</strong> Il ne s&apos;agit pas 
              d&apos;une vente aux enchères publiques. Le vendeur reste libre du choix de l&apos;acquéreur 
              et peut refuser l&apos;ensemble des offres.
            </p>
          </div>
        </div>
      </div>

      {/* Pros Only Banner */}
      {lot.reserve_aux_pros && (
        <div className="bg-blue-50 border-b border-blue-200">
          <div className="container mx-auto px-4 py-2">
            <p className="text-sm text-blue-700 flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <strong>Vente réservée aux professionnels</strong>
            </p>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-6">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
          <Link to="/" className="hover:text-[#0A2540]">Accueil</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to="/encheres" className="hover:text-[#0A2540]">Enchères</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to={`/encheres/${vacationSlug}`} className="hover:text-[#0A2540] truncate max-w-[150px]">
            {vacation.titre_fr}
          </Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-[#0A2540] font-medium truncate max-w-[150px]">{lot.titre_fr}</span>
        </nav>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content - 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image Gallery */}
            <Card>
              <CardContent className="p-4">
                {lot.photos?.length > 0 ? (
                  <div>
                    <div className="relative aspect-video mb-4 bg-gray-100 rounded-lg overflow-hidden">
                      <img 
                        src={getFileUrl(lot.photos[currentImageIndex]?.url)}
                        alt={lot.photos[currentImageIndex]?.alt_fr || lot.titre_fr}
                        className="w-full h-full object-contain"
                      />
                      <Badge className="absolute top-2 left-2 bg-[#0A2540]">
                        Lot {lot.numero_lot}
                      </Badge>
                    </div>
                    {lot.photos.length > 1 && (
                      <div className="grid grid-cols-6 gap-2">
                        {lot.photos.map((photo, idx) => (
                          <button
                            key={idx}
                            onClick={() => setCurrentImageIndex(idx)}
                            className={`aspect-square rounded overflow-hidden border-2 ${
                              idx === currentImageIndex ? 'border-[#F0A22E]' : 'border-transparent'
                            }`}
                          >
                            <img 
                              src={getFileUrl(photo.url)} 
                              alt={photo.alt_fr || `Photo ${idx + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
                    <Gavel className="w-16 h-16 text-gray-300" />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Starting Price */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Mise à prix</p>
                    <p className="text-4xl font-bold text-[#0A2540]">
                      {formatPrice(lot.mise_a_prix)} €
                    </p>
                    <p className="text-sm text-green-600 flex items-center gap-1 mt-1">
                      <CheckCircle className="w-4 h-4" />
                      Commission incluse ({vacation.commission_pct}%)
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Pas d&apos;enchère</p>
                    <p className="text-xl font-semibold text-[#0A2540]">{formatPrice(lot.pas_enchere)} €</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Visit Banner */}
            {lot.mode_visite === 'obligatoire' && (
              <Card className="bg-orange-50 border-orange-200">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Eye className="w-5 h-5 text-orange-600 mt-0.5" />
                    <div>
                      <p className="font-semibold text-orange-800">
                        La visite du matériel est obligatoire pour participer à la vente
                      </p>
                      <p className="text-sm text-orange-700 mt-1">
                        {lot.commune && `${lot.commune}`}{lot.departement && ` (${lot.departement})`}
                      </p>
                      {lot.creneaux_visite?.length > 0 && (
                        <div className="mt-2">
                          <p className="text-sm font-medium text-orange-800">Créneaux disponibles :</p>
                          <ul className="text-sm text-orange-700 mt-1">
                            {lot.creneaux_visite.map((c, i) => (
                              <li key={i}>{c.date} à {c.heure}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <Link to={`/encheres/${vacationSlug}/inscription`}>
                        <Button size="sm" className="mt-3 bg-orange-600 hover:bg-orange-700">
                          S&apos;inscrire à une visite
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {lot.mode_visite === 'facultative' && (
              <Card className="bg-gray-50">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Eye className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="text-gray-700">
                        Visite possible sur rendez-vous — non obligatoire pour enchérir
                      </p>
                      {lot.creneaux_visite?.length > 0 && (
                        <p className="text-sm text-gray-500 mt-1">
                          Créneaux : {lot.creneaux_visite.map(c => `${c.date} à ${c.heure}`).join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {lot.mode_visite === 'sans_visite' && (
              <Card className="bg-gray-50">
                <CardContent className="p-4">
                  <p className="text-gray-600 text-sm">
                    Lot vendu en l&apos;état, sur la base des photos et documents fournis.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Description */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  À propos de ce lot
                  <Badge variant="outline" className="text-xs font-normal">
                    Description rédigée par le vendeur
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose prose-sm max-w-none">
                  <p className="whitespace-pre-wrap">{lot.description_vendeur_fr}</p>
                </div>
              </CardContent>
            </Card>

            {/* Structured Technical Specs */}
            {(lot.marque || lot.modele || lot.annee_mise_service || lot.heures_compteur || lot.etat_general) && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5 text-[#F0A22E]" />
                    Caractéristiques principales
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {lot.marque && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Marque</p>
                        <p className="text-lg font-semibold text-[#0A2540] mt-1">{lot.marque}</p>
                      </div>
                    )}
                    {lot.modele && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Modèle</p>
                        <p className="text-lg font-semibold text-[#0A2540] mt-1">{lot.modele}</p>
                      </div>
                    )}
                    {lot.annee_mise_service && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Année</p>
                        <p className="text-lg font-semibold text-[#0A2540] mt-1">{lot.annee_mise_service}</p>
                      </div>
                    )}
                    {lot.heures_compteur && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Heures compteur</p>
                        <p className="text-lg font-semibold text-[#0A2540] mt-1">{lot.heures_compteur.toLocaleString('fr-FR')} h</p>
                      </div>
                    )}
                    {lot.etat_general && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">État général</p>
                        <p className="text-lg font-semibold text-[#0A2540] mt-1">
                          {lot.etat_general === 'excellent' && '✅ Excellent'}
                          {lot.etat_general === 'bon' && '👍 Bon'}
                          {lot.etat_general === 'correct' && '🔧 Correct'}
                          {lot.etat_general === 'a_reviser' && '⚠️ À réviser'}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Legacy Technical Details */}
            {lot.caracteristiques?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Détails techniques</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {(showAllSpecs ? lot.caracteristiques : lot.caracteristiques.slice(0, 6)).map((carac, idx) => (
                      <div key={idx} className="flex justify-between py-2 border-b border-gray-100">
                        <span className="text-gray-600">{carac.label}</span>
                        <span className="font-medium text-[#0A2540]">{carac.valeur}</span>
                      </div>
                    ))}
                  </div>
                  {lot.caracteristiques.length > 6 && (
                    <Button 
                      variant="ghost" 
                      className="mt-4 w-full"
                      onClick={() => setShowAllSpecs(!showAllSpecs)}
                    >
                      {showAllSpecs ? (
                        <><ChevronUp className="w-4 h-4 mr-2" /> Voir moins</>
                      ) : (
                        <><ChevronDown className="w-4 h-4 mr-2" /> Voir toutes les caractéristiques</>
                      )}
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Documents */}
            {lot.documents?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#F0A22E]" />
                    Documents fournis par le vendeur
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {lot.documents.map((doc, idx) => (
                      <a 
                        key={idx}
                        href={getFileUrl(doc.url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 rounded-lg border hover:bg-gray-50 transition-colors group"
                      >
                        <div className="bg-red-100 p-2 rounded">
                          <FileText className="w-5 h-5 text-red-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{doc.nom}</p>
                          {doc.type_doc && doc.type_doc !== 'autre' && (
                            <p className="text-xs text-gray-500 capitalize">
                              {doc.type_doc.replace('_', ' ')}
                            </p>
                          )}
                        </div>
                        <Download className="w-5 h-5 text-gray-400 group-hover:text-[#F0A22E] transition-colors" />
                      </a>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-3 italic">
                    Ces documents sont fournis par le vendeur sous sa seule responsabilité.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Expert Report */}
            {lot.rapport_etat_auteur && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    Rapport d&apos;état
                    <Badge variant="outline">{lot.rapport_etat_auteur}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-500 italic">
                    GEOTS ne rédige pas ce rapport et n&apos;émet aucune estimation de valeur sur le lot.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Sale Terms */}
            <Card>
              <CardHeader>
                <CardTitle>Modalités de vente</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-gray-500">Mode de vente</p>
                    <p className="font-medium">Vente de gré à gré par courtage</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Début des enchères</p>
                    <p className="font-medium">{formatDate(vacation.date_ouverture_vente)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Fin des enchères</p>
                    <p className="font-medium">{formatDate(vacation.date_cloture_vente)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Commission</p>
                    <p className="font-medium">{vacation.commission_pct}% (incluse dans le prix)</p>
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <p className="text-gray-600">
                    <strong>Critères de choix du vendeur :</strong> Le vendeur retient l&apos;acquéreur 
                    en considérant le montant de l&apos;offre, la sécurité du financement et le délai 
                    d&apos;enlèvement. Il n&apos;est pas tenu de retenir l&apos;offre la plus élevée et peut 
                    refuser l&apos;ensemble des offres.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Linked seller mention */}
            {lot.vendeur_societe_liee && (
              <Card className="bg-yellow-50 border-yellow-200">
                <CardContent className="p-4">
                  <p className="text-sm text-yellow-800">
                    <Info className="w-4 h-4 inline mr-2" />
                    Le vendeur de ce lot est une société liée à l&apos;exploitant de la plateforme.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Location */}
            {(lot.commune || lot.departement) && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    Localisation
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-lg">
                    {lot.commune}{lot.departement && ` (${lot.departement})`}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar - Bid Panel */}
          <div className="lg:col-span-1">
            <div className="sticky top-4 space-y-4">
              {/* Countdown */}
              {countdown && !countdown.expired && vacation.statut === 'vente_en_cours' && (
                <Card className={countdown.isUrgent ? 'bg-[#F0A22E] text-white' : 'bg-[#0A2540] text-white'}>
                  <CardContent className="p-6 text-center">
                    <p className="text-sm opacity-80 mb-2">Fin des enchères dans</p>
                    <div className="flex justify-center gap-2">
                      <div>
                        <p className="text-3xl font-bold">{String(countdown.hours).padStart(2, '0')}</p>
                        <p className="text-xs opacity-60">h</p>
                      </div>
                      <span className="text-3xl font-bold">:</span>
                      <div>
                        <p className="text-3xl font-bold">{String(countdown.minutes).padStart(2, '0')}</p>
                        <p className="text-xs opacity-60">min</p>
                      </div>
                      <span className="text-3xl font-bold">:</span>
                      <div>
                        <p className="text-3xl font-bold">{String(countdown.seconds).padStart(2, '0')}</p>
                        <p className="text-xs opacity-60">sec</p>
                      </div>
                    </div>
                    {countdown.isUrgent && (
                      <p className="text-sm mt-2 animate-pulse">
                        ⚡ Enchère glissante active
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Current Best Offer */}
              <Card>
                <CardContent className="p-6">
                  {lot.meilleure_offre ? (
                    <div className="text-center">
                      <p className="text-sm text-gray-500 mb-1">Meilleure offre</p>
                      <p className="text-4xl font-bold text-[#F0A22E]">
                        {formatPrice(lot.meilleure_offre)} €
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        {lot.nb_offres} offre{lot.nb_offres > 1 ? 's' : ''}
                      </p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <p className="text-sm text-gray-500 mb-1">Mise à prix</p>
                      <p className="text-4xl font-bold text-[#0A2540]">
                        {formatPrice(lot.mise_a_prix)} €
                      </p>
                      <p className="text-sm text-gray-500 mt-1">Aucune offre</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Bid Form */}
              {vacation.statut === 'vente_en_cours' && (
                <Card>
                  <CardHeader>
                    <CardTitle>Placer une offre</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {!user ? (
                      <div className="text-center py-4">
                        <p className="text-gray-600 mb-4">Connectez-vous pour enchérir</p>
                        <Link to={`/encheres/connexion?redirect=/encheres/${vacationSlug}/inscription`}>
                          <Button className="w-full bg-[#F0A22E] hover:bg-[#d89426]">
                            Se connecter
                          </Button>
                        </Link>
                      </div>
                    ) : !inscription ? (
                      <div className="text-center py-4">
                        <p className="text-gray-600 mb-4">Inscrivez-vous à cette vente pour enchérir</p>
                        <Link to={`/encheres/${vacationSlug}/inscription`}>
                          <Button className="w-full">S&apos;inscrire</Button>
                        </Link>
                      </div>
                    ) : inscription.statut !== 'validee' ? (
                      <div className="text-center py-4">
                        <Badge variant="outline" className="mb-2">
                          Dossier {inscription.statut === 'recue' ? 'en cours de validation' : inscription.statut}
                        </Badge>
                        <p className="text-sm text-gray-500">
                          Votre dossier doit être validé pour pouvoir enchérir.
                        </p>
                      </div>
                    ) : lot.mode_visite === 'obligatoire' && !inscription.visite_effectuee ? (
                      <div className="text-center py-4">
                        <Eye className="w-8 h-8 mx-auto text-orange-500 mb-2" />
                        <p className="text-gray-600">
                          La visite est obligatoire. Effectuez une visite pour débloquer les enchères.
                        </p>
                      </div>
                    ) : (
                      <>
                        <div>
                          <Label htmlFor="bid">Votre offre (€)</Label>
                          <Input
                            id="bid"
                            type="number"
                            min={minBid}
                            step={lot.pas_enchere}
                            value={bidAmount}
                            onChange={(e) => setBidAmount(e.target.value)}
                            placeholder={`Min. ${formatPrice(minBid)} €`}
                            className="text-lg"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Offre minimum : {formatPrice(minBid)} €
                          </p>
                        </div>

                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="autoBid" 
                            checked={useAutoBid}
                            onCheckedChange={setUseAutoBid}
                          />
                          <Label htmlFor="autoBid" className="text-sm">
                            Enchère automatique
                          </Label>
                        </div>

                        {useAutoBid && (
                          <div>
                            <Label htmlFor="maxBid">Montant maximum (€)</Label>
                            <Input
                              id="maxBid"
                              type="number"
                              min={minBid}
                              value={maxAutoBid}
                              onChange={(e) => setMaxAutoBid(e.target.value)}
                              placeholder="Ex: 50000"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Le système enchérira pour vous jusqu&apos;à ce montant
                            </p>
                          </div>
                        )}

                        <div className="bg-gray-50 p-3 rounded text-sm text-gray-600">
                          <p className="font-medium mb-1">⚠️ Offre définitive</p>
                          <p>Une fois validée, votre offre ne peut être ni modifiée, ni annulée.</p>
                        </div>

                        <Button 
                          className="w-full bg-[#F0A22E] hover:bg-[#d89426] text-lg py-6"
                          onClick={handleBid}
                          disabled={submitting || !bidAmount}
                        >
                          {submitting ? (
                            <span className="animate-pulse">Envoi...</span>
                          ) : (
                            <>
                              <Gavel className="w-5 h-5 mr-2" />
                              Confirmer mon offre
                            </>
                          )}
                        </Button>

                        {inscription.pseudonyme && (
                          <p className="text-xs text-center text-gray-500">
                            Vous enchérissez en tant que : <strong>{inscription.pseudonyme}</strong>
                          </p>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Bid History */}
              {offres.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Historique des offres</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {offres.map((offre, idx) => (
                        <div 
                          key={idx}
                          className={`flex justify-between items-center py-2 px-3 rounded ${
                            idx === 0 ? 'bg-[#F0A22E]/10' : 'bg-gray-50'
                          }`}
                        >
                          <div>
                            <p className="font-medium text-sm">{offre.pseudonyme}</p>
                            <p className="text-xs text-gray-500">
                              {new Date(offre.created_at).toLocaleTimeString('fr-FR')}
                            </p>
                          </div>
                          <p className={`font-bold ${idx === 0 ? 'text-[#F0A22E]' : 'text-gray-700'}`}>
                            {formatPrice(offre.montant)} €
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* CTA for inscription */}
              {vacation.statut === 'annoncee' && (
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-4 text-center">
                    <Users className="w-8 h-8 mx-auto text-green-600 mb-2" />
                    <p className="font-medium text-green-800 mb-2">
                      Inscriptions ouvertes
                    </p>
                    <p className="text-sm text-green-700 mb-4">
                      Inscrivez-vous maintenant pour participer aux enchères
                    </p>
                    <Link to={`/encheres/${vacationSlug}/inscription`}>
                      <Button className="w-full bg-green-600 hover:bg-green-700">
                        S&apos;inscrire pour participer
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Fixed Bar */}
      {vacation.statut === 'vente_en_cours' && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 z-50">
          <div className="flex items-center justify-between gap-4">
            {countdown && !countdown.expired && (
              <div className={`text-center px-3 py-1 rounded ${countdown.isUrgent ? 'bg-[#F0A22E] text-white' : 'bg-gray-100'}`}>
                <p className="text-lg font-bold">
                  {String(countdown.hours).padStart(2, '0')}:{String(countdown.minutes).padStart(2, '0')}:{String(countdown.seconds).padStart(2, '0')}
                </p>
              </div>
            )}
            {canBid ? (
              <Button 
                className="flex-1 bg-[#F0A22E] hover:bg-[#d89426]"
                onClick={() => document.getElementById('bid')?.focus()}
              >
                <Gavel className="w-4 h-4 mr-2" />
                Enchérir
              </Button>
            ) : (
              <Link to={`/encheres/${vacationSlug}/inscription`} className="flex-1">
                <Button className="w-full">S&apos;inscrire pour participer</Button>
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
